import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { supabase } from './supabaseClient';
import defaultUser from './user-default.png';

function MenteeDashboard() {
   const [userId, setUserId] = useState(null);
   const [fullName, setFullName] = useState('');
   const [fullNameInput, setFullNameInput] = useState('');
   const [bio, setBio] = useState('');
   const [bioInput, setBioInput] = useState('');
   const [profileUrl, setProfileUrl] = useState(null);
   const [uploading, setUploading] = useState(false);
   const [availability, setAvailability] = useState(false);
   const [message, setMessage] = useState('');
   const [messageType, setMessageType] = useState('');
   const [isHoveringProfile, setIsHoveringProfile] = useState(false);

  const displayMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 4000);
  };

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserId(user.uid);
        setAvailability(true);

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setFullName(data.fullName || '');
          setFullNameInput(data.fullName || '');
          setBio(data.bio || '');
          setBioInput(data.bio || '');
        }

        const { data } = supabase.storage
          .from('profile-pictures')
          .getPublicUrl(`profile-pictures/${user.uid}/profile.jpg`);
        setProfileUrl(data.publicUrl);
      } else {
        setUserId(null);
        setFullName('');
        setFullNameInput('');
        setBio('');
        setBioInput('');
        setProfileUrl(null);
        setAvailability(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const uploadProfilePic = useCallback(async (e) => {
    if (!userId) return;
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const filePath = `profile-pictures/${userId}/profile.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);
      setProfileUrl(data.publicUrl);

      displayMessage('Profile picture uploaded successfully!', 'success');
    } catch (err) {
      console.error(err);
      displayMessage('Failed to upload profile picture', 'error');
    } finally {
      setUploading(false);
    }
  }, [userId]);

  const saveFullName = useCallback(async () => {
    if (!userId || !fullNameInput.trim()) {
      displayMessage('Full name cannot be empty', 'error');
      return;
    }
    try {
      await updateDoc(doc(db, 'users', userId), {
        fullName: fullNameInput.trim(),
      });
      setFullName(fullNameInput.trim());
      displayMessage('Full name saved successfully!', 'success');
    } catch (err) {
      console.error(err);
      displayMessage('Failed to save full name', 'error');
    }
  }, [userId, fullNameInput]);

  const saveBio = useCallback(async () => {
    if (!userId || !bioInput.trim()) {
      displayMessage('Bio cannot be empty', 'error');
      return;
    }
    try {
      await updateDoc(doc(db, 'users', userId), {
        bio: bioInput.trim(),
      });
      setBio(bioInput.trim());
      displayMessage('Bio saved successfully!', 'success');
    } catch (err) {
      console.error(err);
      displayMessage('Failed to save bio', 'error');
    }
  }, [userId, bioInput]);

  if (!userId) return <p style={styles.loginPrompt}>Please log in to access your dashboard.</p>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Mentee Dashboard</h1>
        <div style={styles.statusBadge}>
          <div style={{...styles.statusDot, backgroundColor: availability ? '#10b981' : '#ef4444'}}></div>
          <span style={styles.statusText}>{availability ? 'Online' : 'Offline'}</span>
        </div>
      </div>

      <div style={styles.mainGrid}>
        <div style={styles.card}>
          <div style={styles.profileSection}>
            <div 
              style={styles.profileImageWrapper}
              onMouseEnter={() => setIsHoveringProfile(true)}
              onMouseLeave={() => setIsHoveringProfile(false)}
            >
              <img
                src={profileUrl || defaultUser}
                alt="Profile"
                style={styles.profileImage}
                onError={(e) => (e.target.src = defaultUser)}
              />
              <div style={{...styles.imageOverlay, opacity: isHoveringProfile ? 1 : 0}}>
                <label style={styles.uploadLabel}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={uploadProfilePic}
                    disabled={uploading}
                    style={styles.fileInput}
                  />
                  {uploading ? 'Uploading...' : 'Change Photo'}
                </label>
              </div>
            </div>
            <h2 style={styles.profileName}>{fullName || 'Your Name'}</h2>
            {bio && <p style={styles.profileBio}>{bio}</p>}
          </div>
        </div>

        <div style={styles.formCard}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Full Name</label>
            <input
              type="text"
              value={fullNameInput}
              onChange={(e) => setFullNameInput(e.target.value)}
              placeholder="Enter your full name"
              style={styles.input}
            />
            <button onClick={saveFullName} disabled={uploading} style={styles.button}>
              Save Name
            </button>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Bio</label>
            <textarea
              value={bioInput}
              onChange={(e) => setBioInput(e.target.value)}
              placeholder="Tell us about yourself..."
              rows={5}
              style={styles.textarea}
            />
            <button onClick={saveBio} disabled={uploading} style={styles.button}>
              Save Bio
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div style={{
          ...styles.notification,
          background: messageType === 'error' 
            ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
            : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
          color: messageType === 'error' ? '#991b1b' : '#065f46',
        }}>
          {message}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '40px 20px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  loginPrompt: {
    color: '#fff',
    fontSize: '18px',
    textAlign: 'center',
    marginTop: '100px',
  },
  header: {
    maxWidth: '1200px',
    margin: '0 auto 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#fff',
    margin: 0,
    textShadow: '0 2px 10px rgba(0,0,0,0.2)',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255,255,255,0.2)',
    backdropFilter: 'blur(10px)',
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    boxShadow: '0 0 10px currentColor',
  },
  statusText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: '14px',
  },
  mainGrid: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
  },
  card: {
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.5)',
  },
  formCard: {
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '24px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.5)',
  },
  profileSection: {
    textAlign: 'center',
  },
  profileImageWrapper: {
    position: 'relative',
    width: '180px',
    height: '180px',
    margin: '0 auto 24px',
    cursor: 'pointer',
    group: 'profile-wrapper',
  },
  profileImage: {
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '5px solid #fff',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(0,0,0,0.7)',
    borderBottomLeftRadius: '90px',
    borderBottomRightRadius: '90px',
    padding: '12px',
    transition: 'opacity 0.3s ease',
    cursor: 'pointer',
  },
  uploadLabel: {
    color: '#fff',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'block',
  },
  fileInput: {
    display: 'none',
  },
  profileName: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 12px',
  },
  profileBio: {
    fontSize: '15px',
    color: '#6b7280',
    lineHeight: '1.6',
    margin: 0,
  },
  fieldGroup: {
    marginBottom: '28px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    marginBottom: '12px',
  },
  textarea: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.3s ease',
    resize: 'vertical',
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    marginBottom: '12px',
  },
  button: {
    width: '100%',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102,126,234,0.4)',
  },
  notification: {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    padding: '16px 24px',
    borderRadius: '12px',
    fontWeight: '600',
    fontSize: '15px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    maxWidth: '400px',
    animation: 'slideIn 0.3s ease',
    zIndex: 1000,
  },
};

export default MenteeDashboard;
